import { useCallback } from "react";
import { useFormContext } from "react-hook-form";

/**
 * Custom hook for managing form synchronization with delivery/consignee data
 * Return functions to overwrite address values with delivery/consignee data
 */
export default function useNotifyPartyAndConsigneeFormSynchronization(partyType: string) {
  const { setValue, getValues } = useFormContext();

  /**
   * Copies delivery address and contact information to current party
   */
  const overwriteAddressValuesWithDelivery = useCallback(() => {
    const deliveryMappings = [
      ["address_type", "stop1_address_type"],
      ["address_reference_number", "destination_address_reference_number"],
      ["contact_name", "stop1_contact_name"],
      ["contact_phone", "stop1_contact_phone"],
      ["contact_email", "stop1_contact_email"],
      ["address_name", "stop1_address_name"],
      ["address_street", "stop1_street"],
      ["address_street_line2", "stop1_street_line2"],
      ["address_city", "stop1_city"],
      ["address_state", "stop1_state"],
      ["address_country", "stop1_country"],
      ["address_zipcode", "stop1_zipcode"],
      ["airport", "stop1_airport_code"],
      ["seaport", "stop1_seaport_code"],
      ["airport_name", "stop1_airport"],
      ["port_name", "stop1_seaport"],
    ];

    deliveryMappings.forEach(([targetField, sourceField]) => {
      setValue(`schedule.${partyType}.${targetField}`, getValues(`schedule.${sourceField}`));
    });
    setValue(`schedule.${partyType}.form_address_contact_type`, "delivery");
  }, [partyType, setValue, getValues]);

  /**
   * Copies consignee address and contact information to current party
   */
  const overwriteAddressValuesWithConsignee = useCallback(() => {
    const consigneeMappings = [
      ["address_type", "consignee.address_type"],
      ["address_reference_number", "consignee.address_reference_number"],
      ["contact_name", "consignee.contact_name"],
      ["contact_phone", "consignee.contact_phone"],
      ["contact_email", "consignee.contact_email"],
      ["address_name", "consignee.address_name"],
      ["address_street", "consignee.address_street"],
      ["address_street_line2", "consignee.address_street_line2"],
      ["address_city", "consignee.address_city"],
      ["address_state", "consignee.address_state"],
      ["address_country", "consignee.address_country"],
      ["address_zipcode", "consignee.address_zipcode"],
      ["airport", "consignee.airport"],
      ["seaport", "consignee.seaport"],
      ["airport_name", "consignee.airport_name"],
      ["port_name", "consignee.seaport_name"],
    ];

    consigneeMappings.forEach(([targetField, sourceField]) => {
      setValue(`schedule.${partyType}.${targetField}`, getValues(`schedule.${sourceField}`));
    });
    setValue(`schedule.${partyType}.form_address_contact_type`, "consignee");
  }, [partyType, setValue, getValues]);

  return {
    overwriteAddressValuesWithDelivery,
    overwriteAddressValuesWithConsignee,
  };
}
